import { motion } from "framer-motion";
import {
  CheckCircle2, AlertTriangle, Sparkles, Copy, Check, Heart, Flame, Camera, MessageCircle, User,
} from "lucide-react";
import { useState } from "react";
import { ScoreBar } from "./ScoreBar";
import { useLang } from "../i18n/LangContext";
import { toast } from "sonner";

const SCORE_DEFS = [
  { key: "response_chance", invert: false },
  { key: "interest", invert: false },
  { key: "meeting_chance", invert: false },
  { key: "chemistry", invert: false },
  { key: "ghosting_risk", invert: true },
  { key: "profile_strength", invert: false },
  { key: "photo_quality", invert: false },
  { key: "bio_quality", invert: false },
];

const MSG_KEYS = ["safe", "casual", "flirty", "specific"];

const MODE_ICON = {
  conversation: MessageCircle, profile: User, photos: Camera, full: Sparkles,
};

export function StandardReportContent({ doc, lang: forcedLang }) {
  const ctx = useLang();
  const lang = forcedLang || ctx.lang;
  const t = (k) => ctx.t(k);
  const r = doc.report || {};
  const scores = r.scores || {};
  const ModeIcon = MODE_ICON[doc.mode] || Sparkles;
  const [activeMsg, setActiveMsg] = useState("safe");
  const [copiedKey, setCopiedKey] = useState(null);

  const copy = async (key, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      toast.success(t("copied"));
      setTimeout(() => setCopiedKey(null), 1800);
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="dc-card relative overflow-hidden p-8">
        <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-gradient-to-br from-[#ff4458]/25 to-transparent blur-3xl" />
        <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#222636] bg-[#0f1117] px-3 py-1 text-xs text-[#a8adbd]">
              <ModeIcon className="h-3.5 w-3.5 text-[#ff4458]" />
              <span className="capitalize">{doc.mode}</span>
              {doc.contact_name && <><span>·</span><span>{doc.contact_name}</span></>}
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">{r.title || "Report"}</h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#a8adbd]">{r.summary}</p>
            {(doc.tags || []).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2" data-testid="report-tags">
                {(doc.tags || []).map((tag, i) => (
                  <span key={i} className="rounded-full bg-[#222636] px-2.5 py-1 text-xs text-[#a8adbd]">{tag}</span>
                ))}
              </div>
            )}
          </div>
          <div data-testid="overall-score" className="flex flex-col items-center rounded-3xl border border-[#222636] bg-[#0f1117] px-8 py-5">
            <div className="font-display text-5xl font-bold tracking-tighter dc-gradient-text">{doc.overall_score}</div>
            <div className="mt-1 text-xs uppercase tracking-wider text-[#a8adbd]">{t("overallScore")}</div>
          </div>
        </div>
      </motion.div>

      <section className="dc-card p-6 sm:p-8" data-testid="scores-section">
        <div className="mb-5 flex items-center gap-2">
          <Flame className="h-5 w-5 text-[#ff4458]" />
          <h2 className="font-display text-lg font-semibold text-white">{t("scoresTitle")}</h2>
        </div>
        <div className="grid grid-cols-1 gap-x-10 gap-y-5 md:grid-cols-2">
          {SCORE_DEFS.map((s) => (
            <ScoreBar key={s.key} testId={`score-${s.key}`} label={t(`score_${s.key}`)} value={scores[s.key]} invert={s.invert} />
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <SignalCard icon={<CheckCircle2 className="h-5 w-5" />} title={t("positiveSignals")} items={r.positive_signals || []} tone="positive" testId="positive-signals" />
        <SignalCard icon={<AlertTriangle className="h-5 w-5" />} title={t("negativeSignals")} items={r.negative_signals || []} tone="negative" testId="negative-signals" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <TextCard title={t("diagnosis")} icon={<Sparkles className="h-5 w-5 text-[#fdb022]" />} body={r.diagnosis} testId="diagnosis-card" />
        <TextCard title={t("nextStep")} icon={<Heart className="h-5 w-5 text-[#ff4458]" fill="#ff4458" />} body={r.next_step} testId="next-step-card" accent />
      </div>

      <section className="dc-card p-6 sm:p-8" data-testid="messages-section">
        <div className="mb-5 flex items-center gap-2">
          <MessageCircle className="h-5 w-5 text-[#ff4458]" />
          <h2 className="font-display text-lg font-semibold text-white">{t("messageSuggestions")}</h2>
        </div>
        <div className="mb-4 flex flex-wrap gap-2">
          {MSG_KEYS.map((k) => (
            <button key={k} onClick={() => setActiveMsg(k)} data-testid={`msg-tab-${k}`}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-all ${activeMsg === k ? "dc-gradient-bg text-white shadow-[0_8px_20px_rgba(253,38,122,0.3)]" : "bg-[#222636] text-[#a8adbd] hover:text-white"}`}>
              {t(`msg_${k}`)}
            </button>
          ))}
        </div>
        {MSG_KEYS.map((k) => {
          if (k !== activeMsg) return null;
          const value = (r.message_suggestions || {})[k] || "";
          return (
            <div key={k} data-testid={`msg-content-${k}`} className="relative rounded-2xl border border-[#222636] bg-[#0f1117] p-5 pr-14">
              <p className="text-base leading-relaxed text-white">{value || "—"}</p>
              {value && (
                <button onClick={() => copy(k, value)} data-testid={`msg-copy-${k}`}
                  className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-[#222636] text-[#a8adbd] hover:bg-[#ff4458]/15 hover:text-white">
                  {copiedKey === k ? <Check className="h-4 w-4 text-[#32d583]" /> : <Copy className="h-4 w-4" />}
                </button>
              )}
            </div>
          );
        })}
      </section>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <SignalCard icon={<AlertTriangle className="h-5 w-5" />} title={t("donts")} items={r.donts || []} tone="negative" testId="donts-card" />
        <SignalCard icon={<Sparkles className="h-5 w-5" />} title={t("profileTips")} items={r.profile_tips || []} tone="tips" testId="tips-card" />
      </div>
    </div>
  );
}

function SignalCard({ icon, title, items, tone, testId }) {
  const colors = {
    positive: { ring: "text-[#32d583]", bg: "bg-[#32d583]/10", chip: "bg-[#32d583]" },
    negative: { ring: "text-[#fd267a]", bg: "bg-[#fd267a]/10", chip: "bg-[#fd267a]" },
    tips: { ring: "text-[#fdb022]", bg: "bg-[#fdb022]/10", chip: "bg-[#fdb022]" },
  }[tone];
  return (
    <section className="dc-card p-6 sm:p-8" data-testid={testId}>
      <div className="mb-4 flex items-center gap-2">
        <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${colors.bg} ${colors.ring}`}>{icon}</span>
        <h2 className="font-display text-lg font-semibold text-white">{title}</h2>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-[#a8adbd]">—</p>
      ) : (
        <ul className="space-y-2.5">
          {items.map((it, i) => (
            <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-white">
              <span className={`mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full ${colors.chip}`} />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TextCard({ title, icon, body, testId, accent = false }) {
  return (
    <section className={`dc-card relative overflow-hidden p-6 sm:p-8 ${accent ? "ring-1 ring-[#ff4458]/30" : ""}`} data-testid={testId}>
      {accent && <div className="absolute -right-12 -top-12 h-44 w-44 rounded-full bg-gradient-to-br from-[#ff4458]/30 to-transparent blur-3xl" />}
      <div className="relative">
        <div className="mb-3 flex items-center gap-2">{icon}<h2 className="font-display text-lg font-semibold text-white">{title}</h2></div>
        <p className="whitespace-pre-line text-sm leading-relaxed text-[#dcdde6]">{body || "—"}</p>
      </div>
    </section>
  );
}
